import { logPrefix, PROFILE_STORAGE_KEY } from '../sdk/constants';

// based on https://github.com/marcuswestin/stores/cookieStore.js
/**
 * A storage class which emulates[1] Storage (i.e. localStorage). You can use it almost the same way as
 * localStorage or sessionStorage, with a few exceptions (see: key, clear, length). It is meant to be used
 * when localStorage and sessionStorage are not available.
 *
 * [1] - This class does not extend Storage, because when localStorage is disabled, calling super() on Storage will cause a fatal error (i.e. Illegal constructor)
 *
 * @hidden
 * @class CookieStorage
 */
export default class CookieStorage {
  baseDomain: string;
  cookieString: string;
  doc: Document;
  sessionIndicator = '.session';
  expDate: Date | undefined;

  isSession(key: string): boolean {
    const sessionKey = `${key}${this.sessionIndicator}`;

    return (
      document.cookie.indexOf(sessionKey) >= 0 ||
      this.cookieString.indexOf(this.sessionIndicator) >= 0
    );
  }

  constructor(baseDomain: string = '', isSession?: boolean, expDate?: Date) {
    this.baseDomain = baseDomain;
    this.cookieString = isSession ? this.sessionIndicator : '';
    this.doc = document;
    this.expDate = expDate;
  }

  readonly length = 1; // the length is always 1 because this represents a single cookie.

  getItem(key: string) {
    if (!key || !this._has(key)) {
      return null;
    }

    var regexpStr =
      '(?:^|.*;\\s*)' +
      encodeURIComponent(`${key}${this.isSession(key) ? this.sessionIndicator : ''}`).replace(
        /[-.+*]/g,
        '\\$&'
      ) +
      '\\s*\\=\\s*((?:[^;](?!;))*[^;]?).*';

    return JSON.parse(decodeURIComponent(this.doc.cookie.replace(new RegExp(regexpStr), '$1')));
  }

  setItem(key: string, data: string) {
    if (!key) {
      return;
    }
    const escapedData = encodeURIComponent(JSON.stringify(data));
    const escapedKey = encodeURIComponent(
      `${key}${this.isSession(key) ? this.sessionIndicator : ''}`
    );

    const expDate =
      this.expDate && key === PROFILE_STORAGE_KEY
        ? new Date(this.expDate).toUTCString()
        : 'Tue, 19 Jan 2038 03:14:07 GMT';

    this.cookieString = `${escapedKey}=${escapedData}; ${
      this.isSession(key) ? '' : 'expires=' + expDate + '; '
    };${this.baseDomain ? `domain=${this.baseDomain};` : ''} path=/`;
    if (data && escapedData.length > 4000) {
      // a bit of a buffer to be safe
      console.warn(
        `${logPrefix} Unable to save data to key "${key}" due to the size of the data (${this.cookieString.length} bytes is greater than maximum allowed 4 KB)`
      );
      return;
    }

    this.doc.cookie = this.cookieString;

    return data;
  }

  removeItem(key: string) {
    if (!key || !this._has(key)) {
      return;
    }

    this.doc.cookie =
      encodeURIComponent(`${key}${this.isSession(key) ? this.sessionIndicator : ''}`) +
      `=; Max-Age=-99999999;${this.baseDomain ? `domain=${this.baseDomain};` : ''} path=/`;
  }

  clear() {
    console.warn(`${logPrefix} unsupported method, please use .removeItem(key) instead.`);
  }

  key(_: number) {
    console.warn(`${logPrefix} unsupported method, please use getItem(key) instead.`);
    return 'N/A';
  }

  _has(key: string) {
    return (
      new RegExp(
        '(?:^|;\\s*)' + encodeURIComponent(key).replace(/[-.+*]/g, '\\$&') + '\\s*\\='
      ).test(this.doc.cookie) ||
      new RegExp(
        '(?:^|;\\s*)' +
          encodeURIComponent(key + this.sessionIndicator).replace(/[-.+*]/g, '\\$&') +
          '\\s*\\='
      ).test(this.doc.cookie)
    );
  }
}
